/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { createShortUid, type Disposable, noop } from '@amazon/vinyl-util'
import type { CreateCloudQueueOptions } from '../core/cloudQueueOptions'
import type { ManagedPlaylistController, PlaylistEntry } from '../core/PlaylistController'
import { ctx } from '../env/playbackContext'
import type * as CloudQueueModule from './CloudQueueController'
import type {
    CloudQueueController,
    CloudQueueControllerDeps,
    CloudQueueControllerImpl,
    CloudQueueFeed,
    SetCloudQueueOptions,
} from './CloudQueueController'
import { type CloudQueueModeWriter, createCloudQueueModeWriter } from './cloudQueueModeWriter'
import { createQueueEditor, type QueueEditor } from './queueEditor'
import { clearQueueRestoreSnapshot } from './queueRestoreStorage'

const { errorHandler } = ctx('lazyCloudQueueController')

type Command = (controller: CloudQueueControllerImpl) => void

/** The playlist mutations a held cloud queue call can race; held behind it, they keep call order. */
const ORDERED_MUTATIONS = [
    'setDataProvider',
    'replaceProvider',
    'clear',
    'seekToEntry',
    'invalidateNext',
    'notifyQueueMutated',
    'pendingSeek',
    'queueLocal',
    'removeLocal',
    'arrangeLocal',
] as const satisfies readonly (keyof ManagedPlaylistController)[]

const ORDERED_ASYNC_MUTATIONS = [
    'playFeed',
    'restoreProvider',
    'skipNext',
    'skipPrevious',
] as const satisfies readonly (keyof ManagedPlaylistController)[]

type Mutation = (...args: never[]) => unknown

let loading: Promise<typeof CloudQueueModule> | null = null

function loadCloudQueueController(): Promise<typeof CloudQueueModule> {
    loading ??= import('./CloudQueueController')
    return loading
}

/** Starts loading the implementation, so it lands as early as the container that will use it. */
export function preloadCloudQueueController(): void {
    // The facade built on this load reports its failure.
    loadCloudQueueController().catch(noop)
}

/**
 * The {@link CloudQueueController} whose implementation loads off the boot chunk. Its mode writer
 * and queue editor exist from the start, so presses and bindings work at once; every other call
 * made before the load lands is held and runs, in call order, as soon as it does. While any call is
 * held, the playlist mutations it can race are held behind it in the same order, so a `clear()`
 * after a held `startEager` still runs after it. Disposed before the load, it drops what it holds,
 * restores the playlist's own mutations and never builds the implementation.
 */
export class LazyCloudQueueController implements CloudQueueController, Disposable {
    readonly modeWriter: CloudQueueModeWriter

    readonly queueEditor: QueueEditor

    private controller: CloudQueueControllerImpl | null = null

    private readonly held: Command[] = []

    private disposed = false

    private readonly restorePlaylist: () => void

    constructor(deps: CloudQueueControllerDeps) {
        const { playlistController } = deps
        const refetchQueue = () => this.refetchQueue()
        this.modeWriter = createCloudQueueModeWriter({ playlistController, refetchQueue })
        this.queueEditor = createQueueEditor({ playlistController, refetchQueue })
        this.restorePlaylist = this.orderPlaylistMutations(playlistController)
        loadCloudQueueController()
            .then(({ CloudQueueControllerImpl }) => {
                if (this.disposed) return
                this.restorePlaylist()
                const controller = new CloudQueueControllerImpl(deps, this)
                this.controller = controller
                for (const command of this.held.splice(0)) {
                    try {
                        command(controller)
                    } catch (error) {
                        errorHandler(error)
                    }
                }
            })
            .catch(errorHandler)
    }

    private orderPlaylistMutations(playlistController: ManagedPlaylistController): () => void {
        const target = playlistController as unknown as Record<string, Mutation>
        const wrapped: string[] = []
        const wrap = (name: string, call: (direct: Mutation, args: never[]) => unknown) => {
            const direct = target[name]!.bind(playlistController) as Mutation
            wrapped.push(name)
            target[name] = (...args: never[]) =>
                this.held.length ? call(direct, args) : direct(...args)
        }
        for (const name of ORDERED_MUTATIONS) {
            wrap(name, (direct, args) => {
                this.held.push(() => direct(...args))
            })
        }
        for (const name of ORDERED_ASYNC_MUTATIONS) {
            wrap(
                name,
                (direct, args) =>
                    new Promise((resolve, reject) => {
                        this.held.push(() => {
                            ;(direct(...args) as Promise<unknown>).then(resolve, reject)
                        })
                    })
            )
        }
        // The wrappers shadow the prototype's methods, so deleting them restores the originals.
        return () => {
            for (const name of wrapped.splice(0)) Reflect.deleteProperty(target, name)
        }
    }

    private run(command: Command): void {
        if (this.controller) command(this.controller)
        else this.held.push(command)
    }

    private ask<T>(request: (controller: CloudQueueControllerImpl) => Promise<T>): Promise<T> {
        return new Promise((resolve, reject) => {
            this.run((controller) => {
                request(controller).then(resolve, reject)
            })
        })
    }

    setQueue(options: SetCloudQueueOptions): void {
        this.run((controller) => controller.setQueue(options))
    }

    startEager(
        entry: PlaylistEntry,
        cloudQueueOptions: CreateCloudQueueOptions,
        eagerStartId = createShortUid()
    ): string {
        this.run((controller) => controller.startEager(entry, cloudQueueOptions, eagerStartId))
        return eagerStartId
    }

    abandonEager(eagerStartId: string): void {
        this.run((controller) => controller.abandonEager(eagerStartId))
    }

    seekToEntry(entry: PlaylistEntry): void {
        this.run((controller) => controller.seekToEntry(entry))
    }

    refetchQueue(): Promise<void> {
        return this.ask((controller) => controller.refetchQueue())
    }

    installRestoredFeed(feed: CloudQueueFeed, positionSec: number): Promise<void> {
        return this.ask((controller) => controller.installRestoredFeed(feed, positionSec))
    }

    clearRestoreSnapshot(): void {
        clearQueueRestoreSnapshot()
    }

    dispose(): void {
        this.disposed = true
        this.held.length = 0
        this.restorePlaylist()
    }
}
